// API service layer — wraps fetch with auth token injection
import { msalInstance, apiConfig } from '../config/authConfig';

const BASE_URL = apiConfig.baseUrl;

// Cache the initialize promise so it only runs once
let msalInitPromise: Promise<void> | null = null;
function ensureMsalReady(): Promise<void> {
  if (!msalInitPromise) {
    msalInitPromise = msalInstance.initialize();
  }
  return msalInitPromise;
}

async function getAuthToken(): Promise<string | null> {
  if (import.meta.env.DEV && localStorage.getItem('anchor_auth_bypass') === 'true') {
    return localStorage.getItem('anchor_auth_token');
  }

  try {
    await ensureMsalReady();
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return null;
    }

    const response = await msalInstance.acquireTokenSilent({
      scopes: ['openid', 'profile', 'email'],
      account: accounts[0],
    });

    return response.idToken || null;
  } catch {
    try {
      const response = await msalInstance.acquireTokenPopup({
        scopes: ['openid', 'profile', 'email'],
      });
      return response.idToken || null;
    } catch {
      // All token acquisition failed — clear stale MSAL state and force re-login
      const accounts = msalInstance.getAllAccounts();
      for (const acct of accounts) {
        await msalInstance.logoutRedirect({
          account: acct,
          onRedirectNavigate: () => false,
        }).catch(() => {});
      }
      window.dispatchEvent(new CustomEvent('anchor:auth-expired'));
      return null;
    }
  }
}

async function apiFetch<T>(path: string, options: RequestInit = {}, retries = 3): Promise<T> {
  let token = await getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const isReadOnly = !options.method || options.method === 'GET';
  let hasRetried401 = false;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        if (response.status === 401 && !hasRetried401) {
          hasRetried401 = true;
          try {
            await ensureMsalReady();
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length > 0) {
              let refreshed;
              try {
                refreshed = await msalInstance.acquireTokenSilent({
                  scopes: ['openid', 'profile', 'email'],
                  account: accounts[0],
                  forceRefresh: true,
                });
              } catch {
                refreshed = await msalInstance.acquireTokenPopup({
                  scopes: ['openid', 'profile', 'email'],
                  account: accounts[0],
                });
              }
              if (refreshed?.idToken) {
                token = refreshed.idToken;
                headers['Authorization'] = `Bearer ${token}`;
                continue;
              }
            }
          } catch { /* all auth attempts failed */ }
        }

        if (response.status === 401) {
          window.dispatchEvent(new CustomEvent('anchor:auth-expired'));
          throw new Error('Session expired');
        }

        // Retry on 429 for GET requests, honoring Retry-After when the server sends it
        if (isReadOnly && response.status === 429 && attempt < retries) {
          const retryAfter = Number(response.headers.get('Retry-After'));
          const waitMs = Number.isFinite(retryAfter) && retryAfter > 0
            ? Math.min(retryAfter, 15) * 1000
            : 1000 * Math.pow(2, attempt);
          await new Promise(r => setTimeout(r, waitMs));
          continue;
        }

        // Retry on 5xx for GET requests only
        if (isReadOnly && response.status >= 500 && attempt < retries) {
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
          continue;
        }
        const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(error.error || error.message || `API error: ${response.status}`);
      }

      const data = await response.json();
      return data.data ?? data;
    } catch (err) {
      if (isReadOnly && attempt < retries && err instanceof TypeError) {
        // Network error (fetch failed) — retry
        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
        continue;
      }
      throw err;
    }
  }

  throw new Error(`API request failed after ${retries} retries: ${path}`);
}

// Agent fleet
export const getAgents = async () => {
  const result = await apiFetch<any>('/api/v1/agents');
  // Server returns { agents: [], total, pages } — unwrap to just the array
  return Array.isArray(result) ? result : (result.agents || []);
};

export const getAgentStats = async () => {
  const result = await apiFetch<any>('/api/v1/agents');
  // Derive stats from the agents list (no separate /stats endpoint)
  const agents = Array.isArray(result) ? result : (result.agents || []);
  return {
    total: result.total || agents.length,
    online: agents.filter((a: any) => a.status === 'ONLINE').length,
    offline: agents.filter((a: any) => a.status === 'OFFLINE').length,
    error: agents.filter((a: any) => a.status === 'ERROR').length,
    maintenance: agents.filter((a: any) => a.status === 'MAINTENANCE').length,
    lastUpdated: new Date().toISOString(),
  };
};

// Tenant
export const getTenant = (tenantId: string) =>
  apiFetch<any>(`/api/v1/tenants/${tenantId}`);

export const updateTenant = (tenantId: string, data: any) =>
  apiFetch<any>(`/api/v1/tenants/${tenantId}`, { method: 'PUT', body: JSON.stringify(data) });

// Compliance
export const getComplianceSummary = () =>
  apiFetch<any>('/api/v1/compliance/summary');

export const getClientScores = (clientId: string) =>
  apiFetch<any[]>(`/api/v1/compliance/scores/${clientId}`);

export const getCisControls = () =>
  apiFetch<any[]>('/api/v1/compliance/cis-controls');

export const getScoreHistory = (entityType: string, entityId: string, days: number = 30) =>
  apiFetch<any[]>(`/api/v1/compliance/history/${entityType}/${entityId}?days=${days}`);

export const getRemediationItems = (clientId: string) =>
  apiFetch<any>(`/api/v1/compliance/remediation/${clientId}`);

// Verify remediation
export const verifyRemediation = (clientId: string) =>
  apiFetch<any>(`/api/v1/compliance/${clientId}/verify-remediation`, { method: 'POST' });

// Alerts
export const getAlerts = () =>
  apiFetch<any[]>('/api/v1/alerts');

export const dismissAlert = (alertId: string) =>
  apiFetch<any>(`/api/v1/alerts/${alertId}/dismiss`, { method: 'POST' });

// Audit results
export const getAuditResults = (params?: { agentId?: string; limit?: number; offset?: number }) => {
  const qs = new URLSearchParams();
  if (params?.agentId) qs.set('agentId', params.agentId);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  return apiFetch<any[]>(`/api/v1/audit-results?${qs}`);
};

export const getAuditResult = (executionId: string) =>
  apiFetch<any>(`/api/v1/audit-results/${executionId}`);

// PR-1033: the evidence receipt for one check. Redacted unless redact is false.
export const getCheckEvidence = (executionId: string, checkId: string, redact = true) =>
  apiFetch<any>(`/api/v1/audit-results/${encodeURIComponent(executionId)}/evidence?check=${encodeURIComponent(checkId)}&redact=${redact ? '1' : '0'}`);

export const getAuditSummary = () =>
  apiFetch<any>('/api/v1/audit-results/summary');

export const getAgentAuditHistory = (agentId: string, limit = 10) =>
  apiFetch<any[]>(`/api/v1/audit-results/agent/${agentId}?limit=${limit}`);

export const exportAuditResultsCsv = async (params?: { agentId?: string }): Promise<void> => {
  const token = await getAuthToken();
  const qs = new URLSearchParams();
  if (params?.agentId) qs.set('agentId', params.agentId);
  const query = qs.toString();

  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${BASE_URL}/api/v1/audit-results/export${query ? `?${query}` : ''}`, { headers });

  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('anchor:auth-expired'));
      throw new Error('Session expired');
    }
    const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(error.error || `Export failed: ${response.status}`);
  }

  // Extract filename from Content-Disposition header, or use a default
  const disposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch?.[1] || `audit-results-${new Date().toISOString().split('T')[0]}.csv`;

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Users (tenant-scoped)
export const getUsers = (tenantId: string) =>
  apiFetch<any[]>(`/api/v1/tenants/${tenantId}/users`);

export const inviteUser = (tenantId: string, email: string, role: string) =>
  apiFetch<any>(`/api/v1/tenants/${tenantId}/users/invite`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });

export const updateUserRole = (tenantId: string, userId: string, role: string) =>
  apiFetch<any>(`/api/v1/tenants/${tenantId}/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });

// Scripts
export const getScripts = () =>
  apiFetch<any[]>('/api/v1/scripts');

export const getScript = (scriptId: string) =>
  apiFetch<any>(`/api/v1/scripts/${scriptId}`);

export const createScript = (data: { name: string; description: string; category: string; scriptContent: string }) =>
  apiFetch<any>('/api/v1/scripts', { method: 'POST', body: JSON.stringify(data) });

export const deleteScript = (scriptId: string) =>
  apiFetch<any>(`/api/v1/scripts/${scriptId}`, { method: 'DELETE' });

export const toggleScriptAssignment = (data: { scriptId: string; targetType: string; targetId?: string; isEnabled: boolean }) =>
  apiFetch<any>('/api/v1/scripts/assignments/toggle', { method: 'PUT', body: JSON.stringify(data) });

export const getScriptVersions = (scriptId: string) =>
  apiFetch<any[]>(`/api/v1/scripts/${scriptId}/versions`);

export const resetScript = (scriptId: string) =>
  apiFetch<any>(`/api/v1/scripts/${scriptId}/reset`, { method: 'POST' });

// Agent actions
export const synchronizeAgent = (agentId: string) =>
  apiFetch<any>(`/api/v1/agents/${agentId}/synchronize`, { method: 'POST' });

export const archiveAgent = (agentId: string) =>
  apiFetch<any>(`/api/v1/agents/${agentId}/archive`, { method: 'POST' });

export const restoreAgent = (agentId: string) =>
  apiFetch<any>(`/api/v1/agents/${agentId}/restore`, { method: 'POST' });

export const getArchivedAgents = async () => {
  const result = await apiFetch<any>('/api/v1/agents/archived');
  return result.agents || [];
};

// Agent logs
export const getAgentLogs = (agentId: string, params?: { level?: string; search?: string; limit?: number }) => {
  const qs = new URLSearchParams();
  if (params?.level && params.level !== 'all') qs.set('level', params.level);
  if (params?.search) qs.set('search', params.search);
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiFetch<any[]>(`/api/v1/agents/${agentId}/logs${query ? `?${query}` : ''}`);
};

// Agent debug logs
export const getAgentDebugLogs = (agentId: string) =>
  apiFetch<any[]>(`/api/v1/agents/${agentId}/debug-logs`);

// Superadmin
export const getSuperadminTenants = () =>
  apiFetch<any[]>('/api/v1/superadmin/tenants');

export const impersonateTenant = (tenantId: string, reason?: string) =>
  apiFetch<any>('/api/v1/superadmin/impersonate', {
    method: 'POST',
    body: JSON.stringify({ tenantId, reason }),
  });

export const endImpersonation = () =>
  apiFetch<any>('/api/v1/superadmin/end-impersonation', { method: 'POST' });

// Admin health
export const getSystemHealth = () =>
  apiFetch<any>('/api/v1/admin/health');

export const getApiHealth = () =>
  apiFetch<any>('/api/v1/admin/health/api');

export const getDatabaseHealth = () =>
  apiFetch<any>('/api/v1/admin/health/database');

// Agent releases
export const getAgentReleases = () =>
  apiFetch<any[]>('/api/v1/admin/agent-releases');

export const getReleaseStats = (releaseId: string) =>
  apiFetch<any>(`/api/v1/admin/agent-releases/${releaseId}/stats`);

export const updateRelease = (releaseId: string, data: {
  rolloutPercentage?: number;
  isActive?: boolean;
  isMandatory?: boolean;
  targetTenantIds?: string[];
  channel?: string;
}) =>
  apiFetch<any>(`/api/v1/admin/agent-releases/${releaseId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

export const pauseRelease = (releaseId: string, paused: boolean) =>
  apiFetch<any>(`/api/v1/admin/agent-releases/${releaseId}/pause`, {
    method: 'POST',
    body: JSON.stringify({ paused }),
  });

export const publishRelease = async (formData: FormData) => {
  const token = await getAuthToken();

  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // Do NOT set Content-Type — browser sets multipart boundary automatically

  const response = await fetch(`${BASE_URL}/api/v1/admin/agent-releases`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('anchor:auth-expired'));
      throw new Error('Session expired');
    }
    const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  const data = await response.json();
  return data.data ?? data;
};

// Vulnerability scanning
export const triggerVulnScan = () =>
  apiFetch<any>('/api/v1/admin/vulnerabilities/scan', { method: 'POST' });

export const getLatestVulnScan = () =>
  apiFetch<any>('/api/v1/admin/vulnerabilities/latest');

export const getVulnScanHistory = (limit: number = 10) =>
  apiFetch<any[]>(`/api/v1/admin/vulnerabilities/history?limit=${limit}`);

export const getAgentVulnScan = (agentId: string) =>
  apiFetch<any>(`/api/v1/admin/vulnerabilities/agent/${agentId}`);

// Quick Actions — fleet synchronize (triggers turbo mode on all online agents)
export const triggerFleetScan = () =>
  apiFetch<any>('/api/v1/agents/synchronize-fleet', { method: 'POST' });

// Quick Actions — trigger update rollout (activate latest release at 100%)
export const triggerUpdateRollout = async () => {
  const releases = await getAgentReleases();
  const latest = Array.isArray(releases) ? releases[0] : null;
  if (!latest) throw new Error('No releases available');
  if (!latest.is_active) throw new Error('No active release published');
  return updateRelease(latest.id, { rolloutPercentage: 100 });
};

// Quick Actions — download posture report CSV
export const downloadPostureReportCsv = async () => {
  const token = await getAuthToken();

  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${BASE_URL}/api/v1/audit-results/export/csv`, { headers });
  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('anchor:auth-expired'));
      throw new Error('Session expired');
    }
    const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(error.error || `Export failed: ${response.status}`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `posture-report-${new Date().toISOString().split('T')[0]}.csv`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

// Tenant clients (GET /tenants/:id/clients). Server wraps in { data }; apiFetch unwraps.
export const getTenantClients = async (tenantId: string): Promise<Array<{ id: string; name: string }>> => {
  const result = await apiFetch<any>(`/api/v1/tenants/${tenantId}/clients`);
  const list = Array.isArray(result) ? result : (result?.clients || []);
  return list.map((c: any) => ({ id: c.id, name: c.name }));
};

// Billing (read-only; PR-923 server half, Server #145; roster follow-up, Server #146)
export const getBillingTiers = () =>
  apiFetch<{ tiers: Array<{ id: string; code: string; name: string; rank: number; monthlyRateCents: number; stripePriceId: string | null }> }>('/api/v1/billing/tiers');

export const getClientTier = (clientId: string) =>
  apiFetch<{ tier: { id: string; code: string; name: string; rank: number; monthlyRateCents: number; clientId: string; assignedAt: string | null }; modules: string[]; moduleCount: number }>(`/api/v1/billing/clients/${clientId}/tier`);

export const getInvoicePreview = (from: string, to: string) =>
  apiFetch<any>(`/api/v1/billing/invoice-preview?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);

export const getRosterCoverage = (from: string, to: string) =>
  apiFetch<any>(`/api/v1/billing/roster/coverage?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);

export const getBillingAgentReport = (from: string, to: string) =>
  apiFetch<any>(`/api/v1/billing/roster/agents?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);

export const getBillableCount = () =>
  apiFetch<{ billable: number; excludedArchived: number; excludedStale: number; total: number; windowDays: number; asOf: string }>('/api/v1/billing/agents/count');

// Quick Actions — create client
export const createClient = (tenantId: string, data: { name: string; contactEmail?: string }) =>
  apiFetch<any>(`/api/v1/tenants/${tenantId}/clients`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

// Azure Backup audit — refresh=true bypasses the server's 15-min result cache
export const getAzureBackupAudit = (refresh = false) =>
  apiFetch<any>(`/api/v1/azure-backup/audit${refresh ? '?refresh=true' : ''}`);

export const getAzureBackupStatus = () =>
  apiFetch<any>('/api/v1/azure-backup/status');

// Azure consent flow
export const initiateAzureConsent = (redirect?: string) => {
  const qs = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
  return apiFetch<{ consentUrl: string; state: string }>(`/api/v1/azure-consent/initiate${qs}`);
};

export const getAzureConsentStatus = () =>
  apiFetch<{
    consented: boolean;
    customerTenantId?: string;
    subscriptions?: Array<{ id: string; name: string }>;
    consentedAt?: string;
  }>('/api/v1/azure-consent/status');

export const revokeAzureConsent = () =>
  apiFetch<{ revoked: boolean }>('/api/v1/azure-consent/revoke', { method: 'POST' });

export const refreshAzureSubscriptions = () =>
  apiFetch<{
    subscriptionCount: number;
    subscriptions: Array<{ id: string; name: string }>;
  }>('/api/v1/azure-consent/refresh-subscriptions', { method: 'POST' });

// Azure audit exclusions
export const getAuditExclusions = () =>
  apiFetch<any[]>('/api/v1/azure-backup/exclusions');

export const addAuditExclusion = (data: {
  resourceKey: string;
  resourceType: 'unprotected_vm' | 'protected_item';
  resourceName?: string;
  justification: string;
}) =>
  apiFetch<{ excluded: boolean }>('/api/v1/azure-backup/exclusions', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const removeAuditExclusion = (id: string) =>
  apiFetch<{ removed: boolean }>(`/api/v1/azure-backup/exclusions/${id}`, { method: 'DELETE' });

// Azure restore testing
export const triggerRestoreTest = (testType: 'disk' | 'full' = 'disk') =>
  apiFetch<{ runId: string; itemName: string }>('/api/v1/azure-backup/restore-test', {
    method: 'POST',
    body: JSON.stringify({ testType }),
  });

export const getRestoreTests = () =>
  apiFetch<any[]>('/api/v1/azure-backup/restore-tests');

export const getRestoreTestConfig = () =>
  apiFetch<any>('/api/v1/azure-backup/restore-test/config');

export const saveRestoreTestConfig = (cfg: {
  enabled: boolean;
  frequencyDays: number;
  stagingResourceGroup: string;
  itemId?: string;
  testType: 'disk' | 'full';
  maxLifetimeMinutes: number;
}) =>
  apiFetch<{ saved: boolean }>('/api/v1/azure-backup/restore-test/config', {
    method: 'PUT',
    body: JSON.stringify(cfg),
  });

// Deployment info
export const getDeploymentInfo = () =>
  apiFetch<{
    serverVersion: string;
    nodeVersion: string;
    environment: string;
    region: string;
    uptime: number;
    gitCommit: string;
  }>('/api/v1/deployment-info');

// Reports
export const getReportHistory = () =>
  apiFetch<any[]>('/api/v1/reports');

export const generateReport = async (reportType: string, clientId?: string) => {
  const token = await getAuthToken();

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${BASE_URL}/api/v1/reports/generate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ reportType, clientId }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('anchor:auth-expired'));
      throw new Error('Session expired');
    }
    const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(error.error || `Report generation failed: ${response.status}`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `report-${new Date().toISOString().split('T')[0]}.pdf`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);

  return response.headers.get('X-Report-Id');
};

export const downloadReport = async (reportId: string) => {
  const token = await getAuthToken();

  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${BASE_URL}/api/v1/reports/${reportId}/download`, { headers });
  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('anchor:auth-expired'));
      throw new Error('Session expired');
    }
    throw new Error(`Download failed: ${response.status}`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `report.pdf`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
